import React from 'react'
import PropTypes from 'prop-types'

import { Grid, Typography } from '@material-ui/core'

import { TrackButton } from 'artkive/components/Tracker'
import { GET_BOX_CLICK } from 'artkive/constants/tracker/mainNavigation'
import useMobileBreakpoint from 'artkive/hooks/useMobileBreakpoint'
import useSale from 'artkive/hooks/useSale'

import { MOBILE_IMAGE_RATIO } from './Keepsakes.constants'
import Phrases from './Phrases'
import {
  BodyWrapper,
  Card,
  Column,
  CtaRow,
  LargeImage,
  LargeText,
  Section,
  SMALL_IMAGE_SIZE,
  SmallCard,
  SmallImage,
  SmallText,
  Subtitle,
} from './RecipientCards.style'

const LARGE_IMAGE_RATIO = 580 / 400

const RecipientCards = ({ title, subtitle, lead, groups, cta, ...rest }) => {
  const isMobile = useMobileBreakpoint()
  const { boxCheckoutUrl } = useSale()

  const largeGroups = groups.filter(({ size }) => size !== 'small')
  const smallGroups = groups.filter(({ size }) => size === 'small')

  return (
    <Section
      title={
        <Typography variant='h2' align='center'>
          {title}
        </Typography>
      }
      subtitle={
        <>
          <Subtitle variant='subtitle1'>{subtitle}</Subtitle>
          {lead && <Subtitle variant='subtitle1'>{lead}</Subtitle>}
        </>
      }
      {...rest}
    >
      <Grid container spacing={isMobile ? 3 : 5}>
        {largeGroups.map(({ title: groupTitle, body, image, alt }) => (
          <Grid xs={12} md={6} item key={groupTitle}>
            <Card>
              <LargeImage ratio={isMobile ? MOBILE_IMAGE_RATIO : LARGE_IMAGE_RATIO} src={image} alt={alt} />
              <LargeText>
                <Typography variant='h3'>
                  <Phrases text={groupTitle} />
                </Typography>
                <BodyWrapper>
                  <Typography variant='body1'>{body}</Typography>
                </BodyWrapper>
              </LargeText>
            </Card>
          </Grid>
        ))}
        {smallGroups.length > 0 && (
          <Grid xs={12} md={6} item>
            <Column>
              {smallGroups.map(({ title: groupTitle, body, image, alt }) => (
                <SmallCard key={groupTitle}>
                  <SmallImage src={image} alt={alt} width={SMALL_IMAGE_SIZE} height={SMALL_IMAGE_SIZE} loading='lazy' />
                  <SmallText>
                    <Typography variant='h3'>
                      <Phrases text={groupTitle} />
                    </Typography>
                    <BodyWrapper>
                      <Typography variant='body1'>{body}</Typography>
                    </BodyWrapper>
                  </SmallText>
                </SmallCard>
              ))}
            </Column>
          </Grid>
        )}
      </Grid>
      <CtaRow>
        <TrackButton
          color='primary'
          href={boxCheckoutUrl}
          size='large'
          variant='contained'
          trackerEvent={GET_BOX_CLICK}
          trackerOptions={{ section: cta.section }}
        >
          {cta.label}
        </TrackButton>
      </CtaRow>
    </Section>
  )
}

RecipientCards.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  lead: PropTypes.string,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      body: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
      size: PropTypes.oneOf(['large', 'small']),
    }),
  ).isRequired,
  cta: PropTypes.shape({
    label: PropTypes.string.isRequired,
    section: PropTypes.string.isRequired,
  }).isRequired,
}

export default RecipientCards
